import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Building2, CreditCard, Plus, Pencil, Trash2, Save, X, Lock, Landmark, Hash, MapPin, Calendar } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const BANK_ACCOUNT_TYPES = ['Savings', 'Current', 'NRO', 'NRE'];
const CARD_NETWORKS = ['Visa', 'Mastercard', 'AMEX', 'RuPay', 'Diners'];

function AccountCard({ account, onEdit, onDelete, onView }) {
  const isBankAccount = account.card_type === 'bank_account';
  return (
    <div data-testid={`account-card-${account.id}`}
      className="border border-[#E2E0D8] rounded-lg p-5 bg-white hover:border-[#284A33]/30 transition-all group cursor-pointer"
      onClick={() => onView(account)}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2.5">
          <div className={`w-9 h-9 rounded-md flex items-center justify-center ${isBankAccount ? 'bg-blue-50 text-blue-600' : 'bg-amber-50 text-amber-600'} ${account.status === 'closed' ? 'opacity-50' : ''}`}>
            {isBankAccount ? <Landmark size={18} /> : <CreditCard size={18} />}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold text-[#1C1C19]">{account.card_name || account.issuer}</h3>
              {account.status === 'closed' && (
                <span className="text-[9px] bg-red-100 text-red-600 px-1.5 py-0.5 rounded font-mono uppercase">Closed</span>
              )}
            </div>
            <p className="text-[11px] text-[#5C5C55] uppercase tracking-wide">{account.issuer}</p>
          </div>
        </div>
        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button onClick={(e) => { e.stopPropagation(); onEdit(account); }} data-testid={`edit-account-${account.id}`}
            className="p-1.5 rounded text-[#5C5C55] hover:text-[#284A33] hover:bg-[#284A33]/10"><Pencil size={13} /></button>
          <button onClick={(e) => { e.stopPropagation(); onDelete(account); }} data-testid={`delete-account-${account.id}`}
            className="p-1.5 rounded text-[#5C5C55] hover:text-red-600 hover:bg-red-50"><Trash2 size={13} /></button>
        </div>
      </div>
      <div className="space-y-1.5 text-[12px]">
        {isBankAccount ? (
          <>
            <DetailRow icon={Hash} label="Account No" value={account.account_number || account.last_four || '—'} />
            <DetailRow icon={Landmark} label="Type" value={account.account_type || '—'} />
            <DetailRow icon={MapPin} label="IFSC" value={account.ifsc_code || '—'} />
            {account.branch && <DetailRow icon={MapPin} label="Branch" value={account.branch} />}
            {account.pdf_password && <DetailRow icon={Lock} label="PDF Password" value="••••••" />}
            {account.active_from && <DetailRow icon={Calendar} label="Started On" value={account.active_from} />}
            {account.active_until && <DetailRow icon={Calendar} label="Closed On" value={account.active_until} />}
          </>
        ) : (
          <>
            <DetailRow icon={Hash} label="Card No" value={account.last_four ? `•••• •••• •••• ${account.last_four}` : '—'} />
            <DetailRow icon={CreditCard} label="Network" value={account.account_type || '—'} />
            <DetailRow icon={Landmark} label="Statement Day" value={account.statement_day ? `${account.statement_day}th` : '—'} />
            {account.active_from && <DetailRow icon={Calendar} label="Started On" value={account.active_from} />}
            {account.active_until && <DetailRow icon={Calendar} label="Closed On" value={account.active_until} />}
          </>
        )}
        <div className="flex items-center gap-1 pt-1">
          <span className="text-[10px] text-[#5C5C55]/60 font-mono uppercase">{account.region}</span>
          <span className="text-[10px] text-[#5C5C55]/60">•</span>
          <span className="text-[10px] text-[#5C5C55]/60 font-mono">{account.statement_count || 0} statements</span>
        </div>
      </div>
    </div>
  );
}

function DetailRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-2 text-[#5C5C55]">
      <Icon size={12} className="text-[#5C5C55]/50 shrink-0" />
      <span className="text-[#5C5C55]/70 w-20 shrink-0">{label}</span>
      <span className="text-[#1C1C19] font-mono">{value}</span>
    </div>
  );
}

const emptyBank = { card_name: '', issuer: '', account_number: '', account_type: 'Savings', ifsc_code: '', branch: '', region: 'india', pdf_password: '', card_type: 'bank_account', last_four: '', statement_day: 1, active_from: '', active_until: '' };
const emptyCard = { card_name: '', issuer: '', last_four: '', account_type: '', region: 'india', pdf_password: '', card_type: 'credit_card', statement_day: 1, account_number: '', ifsc_code: '', branch: '', active_from: '', active_until: '' };

export default function AccountsPage() {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('bank');
  const [dialog, setDialog] = useState({ open: false, mode: 'add', data: {} });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, account: null });

  const fetchAccounts = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/cards`, { withCredentials: true });
      setAccounts(data);
    } catch { toast.error('Failed to load accounts'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAccounts(); }, [fetchAccounts]);

  const filtered = accounts.filter(a => tab === 'bank' ? a.card_type === 'bank_account' : a.card_type === 'credit_card');

  const openAdd = () => {
    setDialog({ open: true, mode: 'add', data: tab === 'bank' ? { ...emptyBank } : { ...emptyCard } });
  };

  const openEdit = (account) => {
    setDialog({ open: true, mode: 'edit', data: { ...account } });
  };

  const saveAccount = async () => {
    const d = dialog.data;
    try {
      if (dialog.mode === 'add') {
        await axios.post(`${API}/cards`, d, { withCredentials: true });
        toast.success(`${tab === 'bank' ? 'Bank account' : 'Credit card'} added`);
      } else {
        const { id, user_id, statement_count, created_at, ...updates } = d;
        await axios.patch(`${API}/cards/${d.id}`, updates, { withCredentials: true });
        toast.success('Updated');
      }
      setDialog({ open: false, mode: 'add', data: {} });
      fetchAccounts();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const deleteAccount = async (keepTxns) => {
    const a = deleteDialog.account;
    try {
      await axios.delete(`${API}/cards/${a.id}?delete_transactions=${!keepTxns}`, { withCredentials: true });
      toast.success('Deleted');
      setDeleteDialog({ open: false, account: null });
      fetchAccounts();
    } catch { toast.error('Delete failed'); }
  };

  const updateField = (key, val) => setDialog(d => ({ ...d, data: { ...d.data, [key]: val } }));
  const isBankTab = tab === 'bank';

  return (
    <div className="p-6 lg:p-8 space-y-5" data-testid="accounts-page">
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-2xl sm:text-3xl tracking-tight text-[#1C1C19]">Accounts</h2>
        <Button onClick={openAdd} data-testid="add-account-btn"
          className="bg-[#284A33] text-white hover:bg-[#1C3624] text-xs h-8 px-3">
          <Plus size={13} className="mr-1.5" /> Add {isBankTab ? 'Bank Account' : 'Credit Card'}
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex items-center border-b border-[#E2E0D8]" data-testid="accounts-tabs">
        <button onClick={() => setTab('bank')} data-testid="tab-bank"
          className={`px-5 py-2.5 text-sm font-medium transition-colors relative flex items-center gap-2 ${tab === 'bank' ? 'text-[#284A33]' : 'text-[#5C5C55] hover:text-[#1C1C19]'}`}>
          <Landmark size={15} /> Bank Accounts
          <span className="text-[10px] bg-[#E8E6DE] text-[#5C5C55] px-1.5 py-0.5 rounded-full font-mono">{accounts.filter(a => a.card_type === 'bank_account').length}</span>
          {tab === 'bank' && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#284A33] rounded-t" />}
        </button>
        <button onClick={() => setTab('card')} data-testid="tab-card"
          className={`px-5 py-2.5 text-sm font-medium transition-colors relative flex items-center gap-2 ${tab === 'card' ? 'text-[#284A33]' : 'text-[#5C5C55] hover:text-[#1C1C19]'}`}>
          <CreditCard size={15} /> Credit Cards
          <span className="text-[10px] bg-[#E8E6DE] text-[#5C5C55] px-1.5 py-0.5 rounded-full font-mono">{accounts.filter(a => a.card_type === 'credit_card').length}</span>
          {tab === 'card' && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#284A33] rounded-t" />}
        </button>
      </div>

      {/* Account cards */}
      {loading ? (
        <div className="text-center py-12 text-[#5C5C55] text-sm">Loading...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16">
          <div className={`w-14 h-14 rounded-full mx-auto mb-3 flex items-center justify-center ${isBankTab ? 'bg-blue-50' : 'bg-amber-50'}`}>
            {isBankTab ? <Landmark size={24} className="text-blue-400" /> : <CreditCard size={24} className="text-amber-400" />}
          </div>
          <p className="text-sm text-[#5C5C55] mb-1">No {isBankTab ? 'bank accounts' : 'credit cards'} added yet</p>
          <p className="text-xs text-[#5C5C55]/60">Click "Add" to get started</p>
        </div>
      ) : (() => {
        const active = filtered.filter(a => a.status !== 'closed');
        const closed = filtered.filter(a => a.status === 'closed');
        return (
          <div className="space-y-6">
            {active.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {active.map(a => (
                  <AccountCard key={a.id} account={a} onEdit={openEdit}
                    onDelete={(a) => setDeleteDialog({ open: true, account: a })}
                    onView={(a) => navigate(`/accounts/${a.id}`)} />
                ))}
              </div>
            )}
            {closed.length > 0 && (
              <>
                {active.length > 0 && (
                  <div className="flex items-center gap-3">
                    <div className="h-px flex-1 bg-[#E2E0D8]" />
                    <span className="text-[10px] uppercase tracking-widest text-[#5C5C55]/60 font-medium">Closed</span>
                    <div className="h-px flex-1 bg-[#E2E0D8]" />
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 opacity-60">
                  {closed.map(a => (
                    <AccountCard key={a.id} account={a} onEdit={openEdit}
                      onDelete={(a) => setDeleteDialog({ open: true, account: a })}
                      onView={(a) => navigate(`/accounts/${a.id}`)} />
                  ))}
                </div>
              </>
            )}
          </div>
        );
      })()}

      {/* Add/Edit Dialog */}
      <Dialog open={dialog.open} onOpenChange={(o) => !o && setDialog({ ...dialog, open: false })}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              {dialog.data.card_type === 'bank_account' ? <Landmark size={16} className="text-blue-600" /> : <CreditCard size={16} className="text-amber-600" />}
              {dialog.mode === 'add' ? 'Add' : 'Edit'} {dialog.data.card_type === 'bank_account' ? 'Bank Account' : 'Credit Card'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {dialog.data.card_type === 'bank_account' ? (
              <>
                <div>
                  <Label className="text-[#5C5C55] text-xs uppercase tracking-wider mb-1 block">Bank Name *</Label>
                  <Input data-testid="field-issuer" value={dialog.data.issuer || ''} onChange={e => updateField('issuer', e.target.value)}
                    placeholder="e.g. Standard Chartered" className="border-[#E2E0D8]" />
                </div>
                <div>
                  <Label className="text-[#5C5C55] text-xs uppercase tracking-wider mb-1 block">Account Name</Label>
                  <Input data-testid="field-card-name" value={dialog.data.card_name || ''} onChange={e => updateField('card_name', e.target.value)}
                    placeholder="e.g. SCB NRO" className="border-[#E2E0D8]" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[#5C5C55] text-xs uppercase tracking-wider mb-1 block">Account Number *</Label>
                    <Input data-testid="field-account-number" value={dialog.data.account_number || ''} onChange={e => updateField('account_number', e.target.value)}
                      placeholder="e.g. 22810226665" className="border-[#E2E0D8]" />
                  </div>
                  <div>
                    <Label className="text-[#5C5C55] text-xs uppercase tracking-wider mb-1 block">Account Type</Label>
                    <Select value={dialog.data.account_type || 'Savings'} onValueChange={v => updateField('account_type', v)}>
                      <SelectTrigger data-testid="field-account-type" className="border-[#E2E0D8]"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {BANK_ACCOUNT_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[#5C5C55] text-xs uppercase tracking-wider mb-1 block">IFSC Code</Label>
                    <Input data-testid="field-ifsc" value={dialog.data.ifsc_code || ''} onChange={e => updateField('ifsc_code', e.target.value)}
                      placeholder="e.g. SCBL0036001" className="border-[#E2E0D8]" />
                  </div>
                  <div>
                    <Label className="text-[#5C5C55] text-xs uppercase tracking-wider mb-1 block">Branch</Label>
                    <Input data-testid="field-branch" value={dialog.data.branch || ''} onChange={e => updateField('branch', e.target.value)}
                      placeholder="e.g. Mumbai Main" className="border-[#E2E0D8]" />
                  </div>
                </div>
                <div>
                  <Label className="text-[#5C5C55] text-xs uppercase tracking-wider mb-1 block">PDF Password</Label>
                  <Input data-testid="field-password" type="password" value={dialog.data.pdf_password || ''} onChange={e => updateField('pdf_password', e.target.value)}
                    placeholder="Statement password" className="border-[#E2E0D8]" />
                </div>
              </>
            ) : (
              <>
                <div>
                  <Label className="text-[#5C5C55] text-xs uppercase tracking-wider mb-1 block">Card Issuer *</Label>
                  <Input data-testid="field-issuer" value={dialog.data.issuer || ''} onChange={e => updateField('issuer', e.target.value)}
                    placeholder="e.g. AMEX, HDFC, SBI" className="border-[#E2E0D8]" />
                </div>
                <div>
                  <Label className="text-[#5C5C55] text-xs uppercase tracking-wider mb-1 block">Card Name</Label>
                  <Input data-testid="field-card-name" value={dialog.data.card_name || ''} onChange={e => updateField('card_name', e.target.value)}
                    placeholder="e.g. AMEX Platinum" className="border-[#E2E0D8]" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[#5C5C55] text-xs uppercase tracking-wider mb-1 block">Last 4 Digits *</Label>
                    <Input data-testid="field-last-four" value={dialog.data.last_four || ''} onChange={e => updateField('last_four', e.target.value)}
                      placeholder="e.g. 1234" maxLength={4} className="border-[#E2E0D8]" />
                  </div>
                  <div>
                    <Label className="text-[#5C5C55] text-xs uppercase tracking-wider mb-1 block">Network</Label>
                    <Select value={dialog.data.account_type || ''} onValueChange={v => updateField('account_type', v)}>
                      <SelectTrigger data-testid="field-network" className="border-[#E2E0D8]"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        {CARD_NETWORKS.map(n => <SelectItem key={n} value={n}>{n}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="text-[#5C5C55] text-xs uppercase tracking-wider mb-1 block">Statement Day</Label>
                  <Input data-testid="field-statement-day" type="number" min={1} max={31} value={dialog.data.statement_day || 1}
                    onChange={e => updateField('statement_day', parseInt(e.target.value) || 1)} className="border-[#E2E0D8] w-20" />
                </div>
              </>
            )}
            <div>
              <Label className="text-[#5C5C55] text-xs uppercase tracking-wider mb-1 block">Region</Label>
              <Select value={dialog.data.region || 'india'} onValueChange={v => updateField('region', v)}>
                <SelectTrigger data-testid="field-region" className="border-[#E2E0D8] w-32"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="india">India</SelectItem>
                  <SelectItem value="uae">UAE</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {/* Account lifecycle dates */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[#5C5C55] text-xs uppercase tracking-wider mb-1 block">Started On</Label>
                <Input data-testid="field-active-from" type="month" value={dialog.data.active_from || ''}
                  onChange={e => updateField('active_from', e.target.value)}
                  className="border-[#E2E0D8]" />
              </div>
              <div>
                <Label className="text-[#5C5C55] text-xs uppercase tracking-wider mb-1 block">Closed On</Label>
                <Input data-testid="field-active-until" type="month" value={dialog.data.active_until || ''}
                  onChange={e => updateField('active_until', e.target.value)}
                  className="border-[#E2E0D8]" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialog({ ...dialog, open: false })}>Cancel</Button>
            <Button onClick={saveAccount} data-testid="save-account-btn" className="bg-[#284A33] text-white hover:bg-[#1C3624]">
              <Save size={13} className="mr-1.5" /> {dialog.mode === 'add' ? 'Add' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteDialog.open} onOpenChange={(o) => !o && setDeleteDialog({ open: false, account: null })}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading text-red-600">Delete {deleteDialog.account?.card_name}?</DialogTitle>
            <DialogDescription>What should happen to linked statements and transactions?</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Button onClick={() => deleteAccount(true)} variant="outline" className="w-full justify-start text-sm" data-testid="delete-keep-txns">
              Keep transactions, remove account only
            </Button>
            <Button onClick={() => deleteAccount(false)} variant="destructive" className="w-full justify-start text-sm" data-testid="delete-all">
              Delete everything (account + statements + transactions)
            </Button>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteDialog({ open: false, account: null })}>Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
